import { api } from '../network/api';
import { authStore, AuthUser } from '../state/auth-store';

interface AuthResponse {
  user: AuthUser;
}

export async function register(email: string, password: string, altcha: string, username?: string): Promise<AuthUser> {
  authStore.set((s) => ({ ...s, status: 'authenticating', error: null }));
  try {
    const res = await api.post<AuthResponse>('/auth/register', { email, password, username, altcha });
    authStore.set({ user: res.user, status: 'authenticated', error: null });
    return res.user;
  } catch (e) {
    authStore.set((s) => ({ ...s, status: 'error', error: (e as Error).message }));
    throw e;
  }
}

export async function login(email: string, password: string, altcha: string): Promise<AuthUser> {
  authStore.set((s) => ({ ...s, status: 'authenticating', error: null }));
  try {
    const res = await api.post<AuthResponse>('/auth/login', { email, password, altcha });
    authStore.set({ user: res.user, status: 'authenticated', error: null });
    return res.user;
  } catch (e) {
    authStore.set((s) => ({ ...s, status: 'error', error: (e as Error).message }));
    throw e;
  }
}

export async function logout(): Promise<void> {
  try { await api.post('/auth/logout'); } catch { /* ignore */ }
  authStore.set({ user: null, status: 'idle', error: null });
}

export async function bootstrapSession(): Promise<AuthUser | null> {
  try {
    const res = await api.post<{ user: AuthUser }>('/auth/refresh', {}, { skipAuthRetry: true });
    authStore.set({ user: res.user, status: 'authenticated', error: null });
    return res.user;
  } catch {
    authStore.set({ user: null, status: 'idle', error: null });
    return null;
  }
}

export async function verifyEmail(token: string): Promise<void> {
  await api.post('/auth/verify-email', { token });
}

/** Re-send the verification link to the signed-in user's own address. */
export async function resendVerification(): Promise<{ sent: boolean; alreadyVerified: boolean }> {
  return api.post<{ sent: boolean; alreadyVerified: boolean }>('/auth/resend-verification');
}

export async function forgotPassword(email: string, altcha: string): Promise<void> {
  await api.post('/auth/forgot-password', { email, altcha });
}

export async function resetPassword(token: string, newPassword: string, altcha: string): Promise<void> {
  await api.post('/auth/reset-password', { token, newPassword, altcha });
}
